import type { ChartDimensions } from '../../types/charts.ts';

/**
 * Compute layout dimensions from container size and margin preferences.
 */
export function computeLayout(
  width: number,
  height: number,
  margins?: Partial<{ top: number; right: number; bottom: number; left: number }>
): ChartDimensions {
  const top = margins?.top ?? 20;
  const right = margins?.right ?? 20;
  const bottom = margins?.bottom ?? 40;
  const left = margins?.left ?? 50;

  return {
    width,
    height,
    top,
    right,
    bottom,
    left,
    innerWidth: Math.max(0, width - left - right),
    innerHeight: Math.max(0, height - top - bottom),
  };
}

/**
 * Measure the approximate pixel width of a text string using a canvas context.
 * Falls back to a character-width estimate if no context is available.
 */
export function measureText(
  text: string,
  fontSize = 12,
  fontFamily = 'system-ui, sans-serif',
  ctx?: CanvasRenderingContext2D
): number {
  if (ctx) {
    ctx.font = `${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
  }
  // Rough estimate: average char ≈ 0.6 × fontSize
  return text.length * fontSize * 0.6;
}

/**
 * Format a numeric value for axis labels.
 */
export function formatAxisValue(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
}

/**
 * Format a number with thousands separator for display (tooltips, labels).
 */
export function formatNumber(value: number): string {
  return value.toLocaleString();
}

/** Max rows a multi-series tooltip shows before capping to the top values. */
const MAX_TOOLTIP_ROWS = 10;

/**
 * Cap multi-series tooltip entries to the top `max` by |raw| value. Under the
 * cap, entries pass through in series order. Over it (a high-cardinality
 * group-by renders one row per series), zero rows are dropped, the rest sort
 * by |raw| descending, and `moreCount` reports how many rows were hidden.
 */
export function topTooltipRows<T>(
  entries: Array<{ row: T; raw: number }>,
  max = MAX_TOOLTIP_ROWS
): { rows: T[]; moreCount: number } {
  if (entries.length <= max) return { rows: entries.map((e) => e.row), moreCount: 0 };
  const kept = entries
    .filter((e) => e.raw !== 0)
    .sort((a, b) => Math.abs(b.raw) - Math.abs(a.raw))
    .slice(0, max);
  return { rows: kept.map((e) => e.row), moreCount: entries.length - kept.length };
}
